/**
 * Weekly timesheets, and approving them.
 *
 * The approval decision is the substance. Approving is one click; rejecting
 * asks for a reason and the API refuses without one — a week sent back with no
 * explanation comes straight back unchanged, which is a round trip that helps
 * nobody.
 *
 * **Nobody approves their own week.** The server refuses it; the button is
 * hidden for the same rows so the refusal is never the way somebody finds out.
 */

import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, RotateCcw, Undo2, User, X } from 'lucide-react';
import {
  ApiError,
  Button,
  Modal,
  PageHeader,
  ServerTable,
  StatusChip,
  Textarea,
  api,
  can,
  createFacetsFetcher,
  createServerFetcher,
  formatDate,
  formatDateTime,
  formatDuration,
  toast,
  useCurrentUser,
  type ServerColumn,
} from '@connect/web-shared';

import { SHEET_STATUS_TONES } from '../../lib/tones';

export interface TimesheetRow {
  public_id: string;
  week_starting: string;
  status: string;
  employee_name: string;
  total_minutes: number;
  total_hours: string;
  billable_minutes: number;
  entry_count: number;
  submitted_at: string | null;
  approved_at: string | null;
  approved_by_name: string | null;
  rejection_reason: string | null;
  employee_public_id: string;
}

interface TimesheetOptions {
  statuses: { value: string; label: string }[];
}

const ENDPOINT = '/timesheets';

export function TimesheetsPage() {
  const queryClient = useQueryClient();
  const user = useCurrentUser();
  const [rejecting, setRejecting] = useState<TimesheetRow | null>(null);
  const [reason, setReason] = useState('');

  const options = useQuery({
    queryKey: ['timesheets', 'options'],
    queryFn: () => api.get<TimesheetOptions>(`${ENDPOINT}/options`),
    staleTime: 10 * 60_000,
  });

  const fetchRows = useMemo(() => createServerFetcher<TimesheetRow>(ENDPOINT), []);
  const getFacets = useMemo(() => createFacetsFetcher(ENDPOINT), []);

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['timesheets'] });
    void queryClient.invalidateQueries({ queryKey: ['time-entries'] });
  };

  const decide = useMutation({
    mutationFn: ({
      row,
      approve,
      why,
    }: {
      row: TimesheetRow;
      approve: boolean;
      why?: string;
    }) => api.post(`${ENDPOINT}/${row.public_id}/decide`, { approve, reason: why ?? null }),
    onSuccess: (_data, variables) => {
      invalidate();
      toast.success(variables.approve ? 'Week approved' : 'Week sent back');
      setRejecting(null);
      setReason('');
    },
    onError: (error) => {
      toast.error(
        'Could not record that',
        error instanceof ApiError ? error.message : undefined,
      );
    },
  });

  const submitOwn = useMutation({
    mutationFn: (row: TimesheetRow) => api.post(`${ENDPOINT}/${row.public_id}/submit`),
    onSuccess: () => {
      invalidate();
      toast.success('Week submitted', 'Its entries are frozen until it is decided.');
    },
    onError: (error) => {
      toast.error('Could not submit', error instanceof ApiError ? error.message : undefined);
    },
  });

  const reopen = useMutation({
    mutationFn: (row: TimesheetRow) => api.post(`${ENDPOINT}/${row.public_id}/reopen`),
    onSuccess: () => {
      invalidate();
      toast.success('Week reopened');
    },
    onError: (error) => {
      toast.error('Could not reopen', error instanceof ApiError ? error.message : undefined);
    },
  });

  const columns = useMemo<ServerColumn<TimesheetRow>[]>(
    () => [
      {
        field: 'week_starting',
        header: 'Week',
        filter: 'date',
        width: 150,
        render: (row) => (
          <span className="font-medium">Week of {formatDate(row.week_starting)}</span>
        ),
      },
      {
        field: 'employee_name',
        header: 'Who',
        icon: <User className="size-3" />,
        filter: 'select',
        minWidth: 170,
      },
      {
        field: 'status',
        header: 'Status',
        filter: 'select',
        options: options.data?.statuses,
        width: 130,
        render: (row) => <StatusChip value={row.status} tones={SHEET_STATUS_TONES} />,
      },
      {
        field: 'total_minutes',
        header: 'Total',
        numeric: true,
        width: 110,
        render: (row) => (
          <span className="tabular font-medium">{formatDuration(row.total_minutes)}</span>
        ),
      },
      {
        field: 'billable_minutes',
        header: 'Billable',
        numeric: true,
        width: 110,
        sortable: false,
        render: (row) => (
          <span className="tabular text-muted">{formatDuration(row.billable_minutes)}</span>
        ),
      },
      { field: 'entry_count', header: 'Entries', numeric: true, width: 90, sortable: false },
      {
        field: 'submitted_at',
        header: 'Submitted',
        filter: 'date',
        width: 150,
        format: (value) => (value ? formatDateTime(value as string) : '—'),
      },
      {
        field: 'approved_by_name',
        header: 'Approved by',
        filter: 'select',
        width: 160,
        hiddenByDefault: true,
      },
    ],
    [options.data],
  );

  return (
    <div className="space-y-4">
      <PageHeader
        title="Timesheets"
        description="A week at a time. Approving one frees its hours to be invoiced."
      />

      <ServerTable<TimesheetRow>
        columns={columns}
        fetchRows={fetchRows}
        getFacets={getFacets}
        rowKey={(row) => row.public_id}
        queryKey={['timesheets']}
        stateKey="timesheets.sheets"
        searchPlaceholder="Search by who"
        rowActions={(row) => {
          // Compared by id, not by display name: two people can share a name
          // and a rename would quietly change who a rule applied to.
          const mine = row.employee_public_id === user?.employee_public_id;
          const canApprove = can('timesheets.sheet.approve');
          const actions = [];

          if (row.status === 'draft' && mine) {
            actions.push({
              label: 'Submit for approval',
              icon: <Check className="size-3.5" />,
              onSelect: () => submitOwn.mutate(row),
            });
          }
          if (row.status === 'submitted' && !mine && canApprove) {
            actions.push(
              {
                label: 'Approve',
                icon: <Check className="size-3.5" />,
                onSelect: () => decide.mutate({ row, approve: true }),
              },
              {
                label: 'Send back',
                icon: <X className="size-3.5" />,
                onSelect: () => setRejecting(row),
              },
            );
          }
          if (row.status === 'approved' && canApprove) {
            actions.push({
              label: 'Reopen',
              icon: <RotateCcw className="size-3.5" />,
              // Reopening an approved week whose hours are already invoiced is
              // refused by the API. Offered here because the usual case is a
              // correction before billing.
              onSelect: () => reopen.mutate(row),
            });
          }
          if (row.status === 'rejected' && mine) {
            actions.push({
              label: 'Resubmit',
              icon: <Undo2 className="size-3.5" />,
              onSelect: () => submitOwn.mutate(row),
            });
          }
          return actions;
        }}
        rowDetailFields={(row) => [
          { label: 'Employee', value: row.employee_name },
          { label: 'Total', value: formatDuration(row.total_minutes) },
          { label: 'Billable', value: formatDuration(row.billable_minutes) },
          { label: 'Entries', value: String(row.entry_count) },
          {
            label: 'Submitted',
            value: row.submitted_at ? formatDateTime(row.submitted_at) : 'Not yet',
          },
          {
            label: 'Approved',
            value: row.approved_at ? formatDateTime(row.approved_at) : 'Not yet',
          },
          { label: 'Approved by', value: row.approved_by_name },
          { label: 'Sent back because', value: row.rejection_reason },
        ]}
        emptyTitle="No timesheets"
        emptyDescription="A week appears here as soon as somebody books time into it."
      />

      <Modal
        open={rejecting !== null}
        onClose={() => setRejecting(null)}
        title="Send this week back"
        description={
          rejecting
            ? `${rejecting.employee_name}, week of ${formatDate(rejecting.week_starting)}.`
            : undefined
        }
        size="sm"
        footer={
          <>
            <Button variant="secondary" size="sm" onClick={() => setRejecting(null)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              size="sm"
              loading={decide.isPending}
              disabled={!reason.trim()}
              onClick={() => {
                if (rejecting) {
                  decide.mutate({ row: rejecting, approve: false, why: reason.trim() });
                }
              }}
            >
              Send it back
            </Button>
          </>
        }
      >
        <Textarea
          label="Why"
          required
          rows={3}
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          hint="They see this. A week returned without a reason comes back unchanged."
          placeholder="Thursday looks like it belongs to the other project."
        />
      </Modal>

    </div>
  );
}
